import { BookingTypeEnum } from "@/graphql/types"
import { EmploymentCategory } from "@/views/personal/employments/EmploymentTableConfig"

export const Color = Object.freeze({
  fachkraft: "#33ff33",
  zusatzkraft: "#ffcc33",
  ergaenzungskraft: "#ffff33",
  schwerpunktkraft: "#cccc33",

  // Bookings
  regelbuchung: "#ffff99",
  ferienbuchung1: "#33cc99",
  ferienbuchung2: "#66cc66",
  ferienbuchung3: "#33cc33",
  alternativbuchung1: "#ffcc99",
  alternativbuchung2: "#ffcccc",
  kurzzeitbuchung: "#99ccff",

  werktag: "#ffffff",
  schliesszeit: "#999999",
  Z: "#999999",
  Y: "#999999",
  schliesszeitTeamfortbildung: "#999999",
  krank: "#a5cec6",
  urlaub: "#a5de63",
  fortbildung: "#FF99CC",
  freistellung: "#f6de99",
  mutterschutz: "#a3cc99",
  ferien: "#fdeec1",
  Sunday: "#fab3ab",
  feiertag: "#ec9488",
  Saturday: "#dedede",
  sonstige: "#c0c0c0",
  gleittag: "#88de88",
  fachtagung: "#ff99cc",
  beschaeftigungsverbot: "#ffcc66",
  kindKrank: "#a5cec6",
  seminartag: "#ffc6e2",
  fehlzeit: "#e42525",
  regenerationstag: "#A0C896",
  umwandlungstag: "#BEBE40",

  anwesend: "#71ec7b",
  essen: "#23e923",
  entschuldigt: "#fff481",
  unentschuldigt: "#ff4949",

  schulkind: "#7bc9ff",
  krippenkind: "#ffc97b",
  regelkind: "#c2ff7b",
  migrationKind: "#ff7bf1",
  integrativKind: "#997bff",
  default: "#FFFFFF",
})
export type ColorKey = keyof typeof Color
/**
 * Extracts the rgb values from a hexadecimal color string
 * @param hex A hexadecimal representation of a color
 * @returns An object containing the red, green and blue integer values
 */
export function hexToRgb(hex: string) {
  if (hex.startsWith("#")) hex = hex.slice(1)

  const red = parseInt(hex.substring(0, 2), 16)
  const green = parseInt(hex.substring(2, 4), 16)
  const blue = parseInt(hex.substring(4, 6), 16)

  return { red, green, blue }
}

const darkColors = Object.freeze({
  seminartag: "#cc4a8a",
  beschaeftigungsverbot: "#cc9933",
  fachtagung: "#cc4a8a",
  kindKrank: "#4a8a7a",
  fortbildung: "#cc4a8a",
  freistellung: "#b8a04a",
  krank: "#4a8a7a",
  gleittag: "#4a8a4a",
  mutterschutz: "#4a8a4a",
  regenerationstag: "#4a8a4a",
  sonstige: "#6a6a6a",
  urlaub: "#4a8a4a",
  umwandlungstag: "#8a8a2a",
  schliesszeitTeamfortbildung: "#6a6a6a",
  schliesszeit: "#6a6a6a",
})

export function getColorForTermin(termtype: string | undefined, isDark: boolean): string {
  if (termtype === undefined) return ""

  const colors: Record<string, string> = {
    A: isDark ? darkColors.seminartag : Color.seminartag, // Seminar day (Seminartag)
    B: isDark ? darkColors.beschaeftigungsverbot : Color.beschaeftigungsverbot, // Employment ban (Beschäftigungsverbot)
    C: isDark ? darkColors.fachtagung : Color.fachtagung, // Expert conference (Fachtagung)
    E: isDark ? darkColors.kindKrank : Color.kindKrank, // Child Sick (Erkrankung Kind)
    F: isDark ? darkColors.fortbildung : Color.fortbildung, // Advanced training (Fortbildung)
    G: isDark ? darkColors.freistellung : Color.freistellung, // Absence (Freistellung)
    K: isDark ? darkColors.krank : Color.krank, // Sick (Krank)
    L: isDark ? darkColors.gleittag : Color.gleittag, // Flex day (Gleittag)
    M: isDark ? darkColors.mutterschutz : Color.mutterschutz, // Maternity leave (Mutterschutz)
    R: isDark ? darkColors.regenerationstag : Color.regenerationstag, // Regeneration day (Regenerationstag)
    S: isDark ? darkColors.sonstige : Color.sonstige, // Misc (Sonstiger Termin)
    U: isDark ? darkColors.urlaub : Color.urlaub, // Leave (Urlaub)
    W: isDark ? darkColors.umwandlungstag : Color.umwandlungstag, // Change day (Umwandlungstag)
    Y: isDark ? darkColors.schliesszeitTeamfortbildung : Color.schliesszeitTeamfortbildung, // Closing time for advanced team training (Schließzeit Teamfortbildung)
    Z: isDark ? darkColors.schliesszeit : Color.schliesszeit, // Closing time (Schließzeit)
  }

  return colors[termtype] ?? ""
}

/**
 * Takes a background color and returns the appropriate font color (black or white).
 * @param color The background color
 * @returns Either black or white
 */
export function getColorForBackground(color = "#ffffff"): string {
  const { red, green, blue } = hexToRgb(color)

  // Empty string equals default font color
  return red * 0.299 + green * 0.587 + blue * 0.114 > 170 ? "" : "#ffffff"
}

export function getColor(content: ColorKey): string {
  return Color[content]
}

export function getColorForBooking(bookingTypeEnum: string | undefined): string {
  if (bookingTypeEnum === undefined) return ""

  const colors: Record<string, string> = {
    [BookingTypeEnum.Standard]: Color.regelbuchung,
    [BookingTypeEnum.Vacation_1]: Color.ferienbuchung1,
    [BookingTypeEnum.Vacation_2]: Color.ferienbuchung2,
    [BookingTypeEnum.Vacation_3]: Color.ferienbuchung3,
    [BookingTypeEnum.Alternative_1]: Color.alternativbuchung1,
    [BookingTypeEnum.Alternative_2]: Color.alternativbuchung2,
    [BookingTypeEnum.ShortTime]: Color.kurzzeitbuchung,
  }

  return colors[bookingTypeEnum] ?? ""
}

export function mapCategoryClass(category: EmploymentCategory): string {
  switch (category) {
    case EmploymentCategory.F:
      return "Fachkraft"
    case EmploymentCategory.E:
      return "Ergänzungskraft"
    case EmploymentCategory.Z:
      return "Zusatzkraft"
    case EmploymentCategory.S:
      return "Schwerpunktkraft"
    case EmploymentCategory.A:
      return "Assistenzkraft"
    case EmploymentCategory.P:
      return "Personalbonus"
    case EmploymentCategory.N:
    default:
      return ""
  }
}
